import { useCallback, useEffect, useRef, useState } from 'react';
import { FeatureLayer, Graphic, WebMap } from '../constructors';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import { createGeoPointLocationFeatureLayer } from '../helpers/feature-layers';
import { createGeoPointLocationGraphic } from '../helpers/graphics';
import { areLatLngsEqual } from '../../location/helpers';
import type { GeoPointLocation } from '../types';
import { useLayerControl, usePreviousLayer } from './use-feature-layer-control';
import { EsriMapEntityTypeEnum } from '../types';

type GeoPointLocationFeatureLayerProps = {
    mapViewportGeoPointLocations: GeoPointLocation[];
    selectedGeoPointLocationId?: number;
    selectedMapLedgerEntityType?: EsriMapEntityTypeEnum;
    webMap?: WebMap;
};

export const useGeoPointLocationFeatureLayer = ({
    mapViewportGeoPointLocations,
    selectedGeoPointLocationId,
    selectedMapLedgerEntityType,
    webMap,
}: GeoPointLocationFeatureLayerProps) => {
    const [geoPointLocationLayer, setGeoPointLocationLayer] = useState<FeatureLayer>();
    const prevSelectedGeoPointLocationId = usePreviousLayer(
        selectedGeoPointLocationId,
        geoPointLocationLayer
    );
    const prevSelectedMapLedgerEntityType = usePreviousLayer(
        selectedMapLedgerEntityType,
        geoPointLocationLayer
    );

    const entityIdToObjectIdMapCacheRef = useRef<typeof EntityIdToObjectIdMaps.GEO_POINT_LOCATIONS>(
        EntityIdToObjectIdMaps.GEO_POINT_LOCATIONS
    );

    const didGeoPointLocationGraphicChange = useCallback(
        (currLocation: GeoPointLocation, prevLocation: GeoPointLocation) => {
            const isEntityTypeChanged =
                prevSelectedMapLedgerEntityType !== selectedMapLedgerEntityType &&
                (prevSelectedMapLedgerEntityType === EsriMapEntityTypeEnum.GEO_POINT_LOCATION ||
                    selectedMapLedgerEntityType === EsriMapEntityTypeEnum.GEO_POINT_LOCATION);

            return (
                isEntityTypeChanged ||
                !areLatLngsEqual(prevLocation, currLocation) ||
                (selectedGeoPointLocationId !== prevSelectedGeoPointLocationId &&
                    (currLocation.entityId === selectedGeoPointLocationId ||
                        currLocation.entityId === prevSelectedGeoPointLocationId))
            );
        },
        [
            prevSelectedGeoPointLocationId,
            selectedGeoPointLocationId,
            prevSelectedMapLedgerEntityType,
            selectedMapLedgerEntityType,
        ]
    );

    // handles initializing empty feature layer
    useEffect(() => {
        if (webMap) {
            const geoPointLocationFeatureLayer = createGeoPointLocationFeatureLayer();
            setGeoPointLocationLayer(geoPointLocationFeatureLayer);
            webMap.layers.add(geoPointLocationFeatureLayer);
        }
    }, [webMap]);

    const createGeoPointLocationGraphics = useCallback(
        (mapViewportGeoPointLocations: GeoPointLocation[]) => {
            const geoPointLocationGraphics: Graphic[] = [];
            for (const geoPointLocation of mapViewportGeoPointLocations) {
                const objectId = entityIdToObjectIdMapCacheRef.current.get(
                    geoPointLocation.entityId
                );
                const geoPointLocationGraphic = createGeoPointLocationGraphic({
                    objectId,
                    selectedGeoPointLocationId,
                    geoPointLocation,
                    selectedMapLedgerEntityType,
                });
                if (geoPointLocationGraphic) {
                    geoPointLocationGraphics.push(geoPointLocationGraphic);
                }
            }
            return geoPointLocationGraphics;
        },
        [selectedGeoPointLocationId, selectedMapLedgerEntityType]
    );

    useLayerControl<GeoPointLocation>({
        layer: geoPointLocationLayer,
        currItems: mapViewportGeoPointLocations,
        didItemChange: didGeoPointLocationGraphicChange,
        createGraphics: createGeoPointLocationGraphics,
        entityIdToObjectIdMapCacheRef,
    });

    return { geoPointLocationLayer };
};
